import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { printTicket80mm } from '../utils/ticket80mmPrint';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ModalPortal from '../components/ModalPortal';
import RepairStockSearch from '../features/reparations/RepairStockSearch';
import StockMagasinPicker from '../features/stock/StockMagasinPicker';
import {
  Plus, Edit, Trash2, X, Wrench, Send, Printer, Package, Truck,
} from 'lucide-react';

const emptyPending = () => ({
  designation: '', reference: '', qty: 1, unit_price: '', fournisseur: '', part_type: 'standard',
});

const lineTotal = (l) => (Number(l.qty) || 0) * (Number(l.unit_price) || 0);

const CommandesPage = () => {
  const { api, isAdmin, user } = useAuth();
  const { t, tError, formatCurrency, formatDate } = useLanguage();
  const navigate = useNavigate();
  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCommande, setEditingCommande] = useState(null);
  const [showAcompteModal, setShowAcompteModal] = useState(false);
  const [selectedCommande, setSelectedCommande] = useState(null);
  const [acompteData, setAcompteData] = useState({ montant: 0, mode_paiement: 'Espèces' });
  const [showAcomptesListModal, setShowAcomptesListModal] = useState(false);
  const [acomptesList, setAcomptesList] = useState([]);
  const [magasinsList, setMagasinsList] = useState([]);
  const [magasinId, setMagasinId] = useState(user?.default_magasin_id || 'M1');
  const [clientNom, setClientNom] = useState('');
  const [clientTel, setClientTel] = useState('');
  const [formError, setFormError] = useState('');
  const [lignes, setLignes] = useState([]);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingForm, setPendingForm] = useState(emptyPending());
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingSaving, setTrackingSaving] = useState(false);

  const prixFinal = useMemo(
    () => round2(lignes.reduce((s, l) => s + lineTotal(l), 0)),
    [lignes],
  );

  useEffect(() => {
    fetchCommandes();
    api.get('/magasins?active_only=true')
      .then((r) => {
        const list = r.data || [];
        setMagasinsList(list);
        if (list.length && !list.some((m) => m.code === magasinId)) {
          setMagasinId(list[0].code);
        }
      })
      .catch(() => {});
  }, []);

  const fetchCommandes = async () => {
    try {
      const response = await api.get('/commandes');
      setCommandes(response.data);
    } catch (error) {
      console.error('Error fetching commandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setClientNom('');
    setClientTel('');
    setLignes([]);
    setPendingForm(emptyPending());
    setPendingOpen(false);
    setFormError('');
    setMagasinId(user?.default_magasin_id || magasinsList[0]?.code || 'M1');
  };

  const openCreate = () => {
    resetForm();
    setEditingCommande(null);
    setShowModal(true);
  };

  const openEditModal = (commande) => {
    setEditingCommande(commande);
    setClientNom(commande.client_nom || '');
    setClientTel(commande.client_tel || '');
    setMagasinId(commande.magasin_id || user?.default_magasin_id || 'M1');
    const existing = Array.isArray(commande.lignes) && commande.lignes.length
      ? commande.lignes.map((l) => ({
          id: l.id || `tmp-${Math.random().toString(36).slice(2)}`,
          designation: l.designation,
          reference: l.reference || '',
          qty: l.qty || 1,
          unit_price: Number(l.unit_price) || 0,
          source: l.source || (l.reference ? 'stock' : 'manual'),
          part_type: l.part_type || 'standard',
          fournisseur: l.fournisseur || '',
          stock_out: !!l.stock_out,
          stock_movement_id: l.stock_movement_id || null,
        }))
      : (commande.piece
        ? [{
            id: `legacy-${commande.id}`,
            designation: commande.piece,
            reference: commande.ref_piece || '',
            qty: commande.quantite || 1,
            unit_price: Number(commande.prix_final) || 0,
            source: commande.ref_piece ? 'stock' : 'manual',
            part_type: 'standard',
            fournisseur: commande.fournisseur || '',
            stock_out: false,
            stock_movement_id: null,
          }]
        : []);
    setLignes(existing);
    setPendingOpen(false);
    setFormError('');
    setShowModal(true);
  };

  const addStockPart = (part) => {
    if (!part?.designation && !part?.reference) return;
    setLignes((prev) => {
      const ref = part.reference || '';
      const existingIdx = ref ? prev.findIndex((l) => l.reference === ref && l.source === 'stock') : -1;
      if (existingIdx >= 0) {
        return prev.map((l, i) => (i === existingIdx ? { ...l, qty: (l.qty || 1) + 1 } : l));
      }
      return [...prev, {
        id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        designation: part.designation || part.reference,
        reference: ref,
        qty: 1,
        unit_price: Number(part.unit_price) || 0,
        source: ref ? 'stock' : 'manual',
        part_type: 'standard',
        fournisseur: '',
        // Par défaut : sortir du stock tout de suite (évite stock fantôme)
        stock_out: !!ref,
        stock_movement_id: null,
      }];
    });
  };

  const addPendingPart = () => {
    const designation = (pendingForm.designation || '').trim();
    if (!designation) {
      setFormError(t('cmd.errDesignationRequired'));
      return;
    }
    setLignes((prev) => [...prev, {
      id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      designation,
      reference: (pendingForm.reference || '').trim(),
      qty: Math.max(1, parseInt(pendingForm.qty, 10) || 1),
      unit_price: parseFloat(String(pendingForm.unit_price).replace(',', '.')) || 0,
      source: 'manual',
      part_type: pendingForm.part_type || 'standard',
      fournisseur: (pendingForm.fournisseur || '').trim(),
    }]);
    setPendingForm(emptyPending());
    setPendingOpen(false);
    setFormError('');
  };

  const updateLigne = (id, patch) => {
    setLignes((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const removeLigne = (id) => {
    setLignes((prev) => prev.filter((l) => l.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!clientNom.trim() || !clientTel.trim()) {
      setFormError(t('cmd.errClientPhoneRequired'));
      return;
    }
    if (!lignes.length) {
      setFormError(t('cmd.errAddPart'));
      return;
    }
    const payload = {
      client_nom: clientNom.trim(),
      client_tel: clientTel.trim(),
      magasin_id: magasinId,
      piece: '',
      ref_piece: '',
      fournisseur: '',
      quantite: 1,
      prix_final: prixFinal,
      lignes: lignes.map((l) => ({
        id: String(l.id || '').startsWith('tmp-') ? undefined : l.id,
        designation: l.designation,
        reference: l.reference || '',
        qty: Math.max(1, parseInt(l.qty, 10) || 1),
        unit_price: Number(l.unit_price) || 0,
        source: l.source || 'manual',
        part_type: l.part_type || 'standard',
        fournisseur: l.fournisseur || '',
        stock_out: !!(l.source === 'stock' && l.stock_out),
        stock_movement_id: l.stock_movement_id || null,
      })),
    };
    try {
      if (editingCommande) {
        await api.put(`/commandes/${editingCommande.id}`, payload);
      } else {
        await api.post('/commandes', payload);
      }
      setShowModal(false);
      setEditingCommande(null);
      resetForm();
      fetchCommandes();
    } catch (error) {
      setFormError(tError(error.response?.data?.detail) || t('cmd.error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('cmd.deleteConfirm'))) return;
    try {
      await api.delete(`/commandes/${id}`);
      fetchCommandes();
    } catch (error) {
      alert(t('cmd.errDelete'));
    }
  };

  const handleAnnuler = async (id) => {
    if (!window.confirm(t('cmd.cancelConfirm'))) return;
    try {
      await api.post(`/commandes/${id}/annuler`);
      fetchCommandes();
    } catch (error) {
      alert(t('cmd.error'));
    }
  };

  const handleAddAcompte = async () => {
    if (acompteData.montant <= 0) {
      alert(t('cmd.errInvalidAmount'));
      return;
    }
    const reste = Number(selectedCommande?.reste_a_payer ?? selectedCommande?.prix_final) || 0;
    if (acompteData.montant > reste + 0.001) {
      alert(t('cmd.errDepositExceeds', { reste: formatCurrency(reste) }));
      return;
    }
    try {
      await api.post('/acomptes', {
        commande_id: selectedCommande.id,
        montant: acompteData.montant,
        mode_paiement: acompteData.mode_paiement,
      });
      setShowAcompteModal(false);
      setAcompteData({ montant: 0, mode_paiement: 'Espèces' });
      fetchCommandes();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('cmd.error'));
    }
  };

  const fetchAcomptes = async (id) => {
    try {
      const response = await api.get(`/acomptes?commande_id=${id}`);
      setAcomptesList(response.data);
      setShowAcomptesListModal(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handlePrintCommande = async (commandeId) => {
    try {
      await printTicket80mm(api, { type: 'commande', ref: commandeId });
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('cmd.errPrintTicket'));
    }
  };

  const handlePrintAcompte = async (acompteId) => {
    try {
      await printTicket80mm(api, { type: 'acompte', ref: acompteId });
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('cmd.errPrintDepositTicket'));
    }
  };

  const handleConvertReparation = async (commandeId) => {
    if (!window.confirm(t('cmd.convertConfirm'))) return;
    try {
      const response = await api.post(`/commandes/${commandeId}/convert-reparation`);
      if (response.data.status === 'prefill') {
        navigate('/reparations/nouveau', {
          state: {
            fromCommande: true,
            commandeId,
            prefill: response.data.prefill,
          },
        });
      }
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('cmd.errConvert'));
    }
  };

  const handleSendEmail = async (commande) => {
    const email = prompt(t('cmd.promptClientEmail'), commande.client_email || '');
    if (!email || !email.includes('@')) return;
    try {
      await api.post(`/commandes/${commande.id}/send-email`, { email });
      alert(t('cmd.emailSent', { email }));
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('cmd.errSendEmail'));
    }
  };

  const openTrackingModal = (cmd) => {
    setSelectedCommande(cmd);
    setTrackingNumber(cmd.tracking_number || '');
    setShowTrackingModal(true);
  };

  const saveTracking = async () => {
    if (!selectedCommande?.id) return;
    setTrackingSaving(true);
    try {
      await api.put(`/commandes/${selectedCommande.id}/tracking`, {
        tracking_number: trackingNumber.trim(),
      });
      setShowTrackingModal(false);
      fetchCommandes();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('cmd.errTracking'));
    } finally {
      setTrackingSaving(false);
    }
  };

  const excludeRefs = useMemo(
    () => lignes.filter((l) => l.source === 'stock' && l.reference).map((l) => l.reference),
    [lignes],
  );

  // Affichage des modes de paiement (valeurs backend FR) -> libellés i18n, repli sur la valeur brute.
  const paymentLabels = {
    'Espèces': t('cmd.payCash'),
    'CB': t('cmd.payCard'),
    'Virement': t('cmd.payTransfer'),
    'Chèque': t('cmd.payCheck'),
  };
  const payLabel = (mode) => paymentLabels[mode] || mode;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in" data-testid="commandes-page">
        <h2 className="text-2xl font-bold text-white mb-2">{t('cmd.clientPartsTitle')}</h2>
        <p className="text-sm text-gray-400 mb-6">
          {t('cmd.pageIntro')}
        </p>

        <div className="glass p-4 mb-6 flex justify-between items-center">
          <span className="text-gray-400">{commandes.length} {t('cmd.title').toLowerCase()}</span>
          <button
            type="button"
            onClick={openCreate}
            className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold flex items-center gap-2"
            data-testid="add-commande-btn"
          >
            <Plus className="w-5 h-5" />
            {t('cmd.addOrder')}
          </button>
        </div>

        <div className="glass overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-dark-custom text-sm">
              <thead>
                <tr>
                  <th>{t('common.client')}</th>
                  <th>{t('common.phone')}</th>
                  <th>{t('cmd.piece')}</th>
                  <th>{t('common.total')}</th>
                  <th>{t('cmd.deposits')}</th>
                  <th>{t('cmd.remainingShort')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8">
                      <div className="loading-spinner mx-auto" />
                    </td>
                  </tr>
                ) : commandes.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-400">{t('cmd.noOrders')}</td>
                  </tr>
                ) : (
                  commandes.map((cmd) => (
                    <tr key={cmd.id}>
                      <td className="font-semibold">
                        {cmd.client_nom}
                        {cmd.source === 'chat_devis_sumup' && (
                          <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300 border border-violet-500/30">CADBOT</span>
                        )}
                        {cmd.tracking_number && (
                          <div className="text-[10px] text-violet-300 font-mono mt-0.5">{cmd.tracking_number}</div>
                        )}
                        {cmd.statut === 'en_conversion' && (
                          <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 border border-orange-500/30">{t('cmd.statusConverting')}</span>
                        )}
                        {cmd.statut === 'reparation' && cmd.device_ref && (
                          <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30">{t('cmd.repairBadge', { ref: cmd.device_ref })}</span>
                        )}
                      </td>
                      <td>
                        <a href={`tel:${cmd.client_tel}`} className="text-cyan-400">{cmd.client_tel}</a>
                      </td>
                      <td>
                        <div className="max-w-[220px]">
                          {(cmd.lignes || []).length > 0 ? (
                            <ul className="space-y-0.5">
                              {cmd.lignes.map((l) => (
                                <li key={l.id || l.designation} className="text-xs">
                                  {l.designation}
                                  {l.qty > 1 ? ` ×${l.qty}` : ''}
                                  {l.source === 'manual' && (
                                    <span className="ml-1 text-[10px] text-amber-400">{t('cmd.toOrderShort')}</span>
                                  )}
                                  {l.source === 'stock' && l.stock_out && (
                                    <span className="ml-1 text-[10px] text-emerald-400">{t('cmd.stockOutShort')}</span>
                                  )}
                                  {l.source === 'stock' && !l.stock_out && (
                                    <span className="ml-1 text-[10px] text-gray-500">{t('cmd.inStockShort')}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <>
                              <div>{cmd.piece}</div>
                              {cmd.ref_piece && <div className="text-xs text-gray-400">{cmd.ref_piece}</div>}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="font-bold">{formatCurrency(cmd.prix_final || 0)}</td>
                      <td>
                        <div className="font-semibold text-cyan-300">{formatCurrency(cmd.total_acomptes || 0)}</div>
                        <button
                          type="button"
                          onClick={() => { setSelectedCommande(cmd); fetchAcomptes(cmd.id); }}
                          className="mt-1 px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 text-[10px] font-bold w-full"
                        >
                          {t('cmd.view')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCommande(cmd);
                            setAcompteData({
                              montant: Number(cmd.reste_a_payer) > 0 ? Number(cmd.reste_a_payer) : 0,
                              mode_paiement: 'Espèces',
                            });
                            setShowAcompteModal(true);
                          }}
                          className="mt-1 px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-[10px] font-bold w-full"
                        >
                          {t('cmd.addDeposit')}
                        </button>
                      </td>
                      <td>
                        {cmd.reste_a_payer > 0 ? (
                          <span className="badge-status badge-livre">{formatCurrency(cmd.reste_a_payer)}</span>
                        ) : (
                          <span className="badge-status badge-termine">{t('cmd.settled')}</span>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1 flex-wrap">
                          <button type="button" onClick={() => handleConvertReparation(cmd.id)} className="p-2 rounded-lg bg-orange-500/20 text-orange-400 hover:bg-orange-500/30" title={t('cmd.convertToRepair')}>
                            <Wrench className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => handlePrintCommande(cmd.id)} className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30" title={t('common.print')}>
                            <Printer className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => handleSendEmail(cmd)} className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30" title={t('common.email')}>
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openTrackingModal(cmd)}
                            className="p-2 rounded-lg bg-violet-500/20 text-violet-300 hover:bg-violet-500/30"
                            title={cmd.tracking_number ? t('cmd.trackingWith', { num: cmd.tracking_number }) : t('cmd.trackingNumber')}
                            data-testid={`tracking-btn-${cmd.id}`}
                          >
                            <Truck className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => openEditModal(cmd)} className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => handleAnnuler(cmd.id)} className="p-2 rounded-lg bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30" title={t('common.cancel')}>
                            <X className="w-4 h-4" />
                          </button>
                          {isAdmin && (
                            <button type="button" onClick={() => handleDelete(cmd.id)} className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create / Edit */}
        <ModalPortal isOpen={showModal}>
          <div className="modal-dark w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="modal-dark-header flex items-center justify-between sticky top-0 bg-[var(--rm-surface,#111)] z-10">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-red-400" />
                {editingCommande ? t('cmd.editOrder') : t('cmd.newClientParts')}
              </h3>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="commande-form">
              <StockMagasinPicker magasinsList={magasinsList} value={magasinId} onChange={setMagasinId} compact />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-2">{t('common.client')} *</label>
                  <input type="text" value={clientNom} onChange={(e) => setClientNom(e.target.value)} className="input-dark w-full py-2" required data-testid="cmd-client-nom" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">{t('common.phone')} *</label>
                  <input type="tel" value={clientTel} onChange={(e) => setClientTel(e.target.value)} className="input-dark w-full py-2" required data-testid="cmd-client-tel" />
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-3 space-y-3">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">{t('cmd.partsSectionTitle')}</p>
                <RepairStockSearch
                  api={api}
                  magasinId={magasinId}
                  onSelect={addStockPart}
                  excludeRefs={excludeRefs}
                  placeholder={t('cmd.searchStockPlaceholder')}
                />

                {lignes.length > 0 && (
                  <ul className="space-y-2" data-testid="cmd-lignes-list">
                    {lignes.map((l) => (
                      <li key={l.id} className="rounded-lg border border-white/10 bg-black/30 p-3">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <div className="font-semibold text-sm text-white truncate">{l.designation}</div>
                            <div className="text-[11px] text-gray-500">
                              {l.reference || t('cmd.noRef')}
                              {l.source === 'manual' && <span className="ml-2 text-amber-400">· {t('cmd.toOrder')}</span>}
                              {l.source === 'stock' && <span className="ml-2 text-emerald-400">· {t('cmd.stock')}</span>}
                            </div>
                          </div>
                          <button type="button" onClick={() => removeLigne(l.id)} className="text-gray-500 hover:text-red-400 shrink-0">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-[10px] text-gray-500 uppercase">{t('cmd.qtyShort')}</label>
                            <input
                              type="number"
                              min="1"
                              className="input-dark w-full py-1.5 text-sm"
                              value={l.qty}
                              onChange={(e) => updateLigne(l.id, { qty: parseInt(e.target.value, 10) || 1 })}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-500 uppercase">{t('cmd.unitPriceLabel')}</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="input-dark w-full py-1.5 text-sm"
                              value={l.unit_price}
                              onChange={(e) => updateLigne(l.id, { unit_price: parseFloat(e.target.value) || 0 })}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-500 uppercase">{t('common.total')}</label>
                            <div className="py-1.5 text-sm font-bold text-white">{formatCurrency(lineTotal(l))}</div>
                          </div>
                        </div>
                        {l.source === 'stock' && l.reference && (
                          <div className="mt-3 pt-2 border-t border-white/10 space-y-2" data-testid={`cmd-stock-out-${l.id}`}>
                            <p className="text-[10px] font-bold text-gray-400 uppercase">{t('cmd.stockSection')}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() => updateLigne(l.id, { stock_out: true })}
                                className={`px-3 py-2 rounded-lg text-xs font-bold border text-left min-h-[44px] ${
                                  l.stock_out
                                    ? 'border-emerald-500/50 bg-emerald-950/40 text-emerald-200'
                                    : 'border-white/10 text-gray-400 hover:border-white/20'
                                }`}
                              >
                                {t('cmd.alreadyOut')}
                                <span className="block text-[10px] font-normal opacity-80 mt-0.5">{t('cmd.decrementOnSave')}</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => updateLigne(l.id, { stock_out: false })}
                                className={`px-3 py-2 rounded-lg text-xs font-bold border text-left min-h-[44px] ${
                                  !l.stock_out
                                    ? 'border-amber-500/50 bg-amber-950/30 text-amber-200'
                                    : 'border-white/10 text-gray-400 hover:border-white/20'
                                }`}
                              >
                                {t('cmd.stillInStock')}
                                <span className="block text-[10px] font-normal opacity-80 mt-0.5">{t('cmd.outOnConvert')}</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {!pendingOpen ? (
                  <button
                    type="button"
                    onClick={() => setPendingOpen(true)}
                    className="w-full py-2 rounded-lg border border-dashed border-amber-500/40 text-amber-300 text-sm font-bold hover:bg-amber-950/20 flex items-center justify-center gap-2"
                    data-testid="cmd-add-pending"
                  >
                    <Truck className="w-4 h-4" /> {t('cmd.addPendingBtn')}
                  </button>
                ) : (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-950/20 p-3 space-y-2">
                    <p className="text-xs font-bold text-amber-300">{t('cmd.pendingTitle')}</p>
                    <input className="input-dark w-full py-2" placeholder={t('cmd.designationPlaceholder')} value={pendingForm.designation} onChange={(e) => setPendingForm({ ...pendingForm, designation: e.target.value })} data-testid="cmd-pending-desig" />
                    <div className="grid grid-cols-2 gap-2">
                      <input className="input-dark w-full py-2" placeholder={t('cmd.refOptPlaceholder')} value={pendingForm.reference} onChange={(e) => setPendingForm({ ...pendingForm, reference: e.target.value })} />
                      <input className="input-dark w-full py-2" placeholder={t('cmd.supplier')} value={pendingForm.fournisseur} onChange={(e) => setPendingForm({ ...pendingForm, fournisseur: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="number" min="1" className="input-dark w-full py-2" placeholder={t('cmd.qtyShort')} value={pendingForm.qty} onChange={(e) => setPendingForm({ ...pendingForm, qty: e.target.value })} />
                      <input type="number" step="0.01" className="input-dark w-full py-2" placeholder={t('cmd.salePricePlaceholder')} value={pendingForm.unit_price} onChange={(e) => setPendingForm({ ...pendingForm, unit_price: e.target.value })} />
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => { setPendingOpen(false); setPendingForm(emptyPending()); }} className="flex-1 py-2 rounded-lg bg-gray-700 text-sm font-bold">{t('common.cancel')}</button>
                      <button type="button" onClick={addPendingPart} className="flex-1 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-sm font-bold">{t('common.add')}</button>
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-1">
                <div className="flex justify-between text-sm"><span className="text-gray-400">{t('cmd.partsTotal')}</span><strong className="text-white">{formatCurrency(prixFinal)}</strong></div>
                {editingCommande && (
                  <>
                    <div className="flex justify-between text-sm"><span className="text-gray-400">{t('cmd.depositsPaid')}</span><span className="text-cyan-300">−{formatCurrency(editingCommande.total_acomptes || 0)}</span></div>
                    <div className="flex justify-between text-sm font-bold border-t border-white/10 pt-2 mt-2">
                      <span>{t('cmd.remaining')}</span>
                      <span className="text-emerald-300">{formatCurrency(Math.max(0, prixFinal - (editingCommande.total_acomptes || 0)))}</span>
                    </div>
                  </>
                )}
              </div>

              {formError && <div className="text-sm text-red-400 bg-red-950/30 border border-red-500/30 rounded-lg px-3 py-2">{formError}</div>}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 font-bold">{t('common.cancel')}</button>
                <button type="submit" className="flex-1 py-3 rounded-xl btn-accent font-bold" data-testid="cmd-submit">
                  {editingCommande ? t('common.edit') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </ModalPortal>

        {/* Acompte */}
        <ModalPortal isOpen={showAcompteModal && !!selectedCommande}>
          <div className="modal-dark w-full max-w-md">
            <div className="modal-dark-header flex items-center justify-between">
              <h3 className="font-bold text-lg">{t('cmd.addDepositTitle')}</h3>
              <button type="button" onClick={() => setShowAcompteModal(false)} className="text-gray-400 hover:text-white">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30 space-y-1">
                <div className="text-sm text-gray-400">{t('cmd.orderLabel')} — {selectedCommande?.client_nom}</div>
                <div className="font-bold text-sm">{selectedCommande?.piece}</div>
                <div className="text-xs text-gray-400 flex justify-between pt-1">
                  <span>{t('common.total')} {formatCurrency(selectedCommande?.prix_final || 0)}</span>
                  <span>{t('cmd.alreadyPaid')} {formatCurrency(selectedCommande?.total_acomptes || 0)}</span>
                </div>
                <div className="text-sm font-bold text-emerald-300">
                  {t('cmd.remainingShort')} {formatCurrency(selectedCommande?.reste_a_payer || 0)}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">{t('cmd.depositAmount')}</label>
                <input
                  type="number"
                  value={acompteData.montant}
                  onChange={(e) => setAcompteData({ ...acompteData, montant: parseFloat(e.target.value) || 0 })}
                  className="input-dark w-full py-3"
                  step="0.01"
                  min="0"
                  max={selectedCommande?.reste_a_payer || undefined}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">{t('cmd.paymentMode')}</label>
                <select
                  value={acompteData.mode_paiement}
                  onChange={(e) => setAcompteData({ ...acompteData, mode_paiement: e.target.value })}
                  className="input-dark w-full py-3"
                >
                  <option value="Espèces">{t('cmd.payCash')}</option>
                  <option value="CB">{t('cmd.payCard')}</option>
                  <option value="Virement">{t('cmd.payTransfer')}</option>
                  <option value="Chèque">{t('cmd.payCheck')}</option>
                </select>
              </div>

              {acompteData.montant > 0 && (
                <div className="text-sm text-gray-300 bg-white/5 rounded-lg px-3 py-2">
                  {t('cmd.newRemainingAfter')}{' '}
                  <strong className="text-white">
                    {formatCurrency(Math.max(0, (selectedCommande?.reste_a_payer || 0) - acompteData.montant))}
                  </strong>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowAcompteModal(false)} className="flex-1 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 font-bold">{t('common.cancel')}</button>
                <button type="button" onClick={handleAddAcompte} className="flex-1 py-3 rounded-xl btn-accent font-bold">{t('common.add')}</button>
              </div>
            </div>
          </div>
        </ModalPortal>

        <ModalPortal isOpen={showAcomptesListModal}>
          <div className="modal-dark w-full max-w-lg">
            <div className="modal-dark-header flex items-center justify-between">
              <h3 className="font-bold text-lg">{t('cmd.depositsPaidTitle')}</h3>
              <button type="button" onClick={() => setShowAcomptesListModal(false)} className="text-gray-400 hover:text-white">✕</button>
            </div>
            <div className="p-6">
              {acomptesList.length === 0 ? (
                <p className="text-gray-400 text-center">{t('cmd.noDeposits')}</p>
              ) : (
                <div className="space-y-3">
                  {acomptesList.map((a) => (
                    <div key={a.id} className="flex justify-between items-center p-3 rounded-xl bg-white/5 border border-white/10">
                      <div>
                        <div className="font-bold">{formatCurrency(a.montant || 0)}</div>
                        <div className="text-xs text-gray-400">{payLabel(a.mode_paiement)} · {a.date_versement ? formatDate(a.date_versement, { dateStyle: 'short', timeStyle: 'short' }) : '—'}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handlePrintAcompte(a.id)}
                        className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400"
                        data-testid={`print-commande-acompte-ticket-${a.id}`}
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ModalPortal>

        <ModalPortal isOpen={showTrackingModal}>
          <div className="modal-dark w-full max-w-md" data-testid="commande-tracking-modal">
            <div className="modal-dark-header flex items-center justify-between">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Truck className="w-5 h-5 text-violet-300" />
                {t('cmd.trackingNumber')}
              </h3>
              <button type="button" onClick={() => setShowTrackingModal(false)} className="text-gray-400 hover:text-white">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-400">
                {t('cmd.orderLabel')} {selectedCommande?.ref_commande || selectedCommande?.client_nom || ''}
              </p>
              <input
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                className="input-dark w-full py-3 font-mono"
                placeholder={t('cmd.trackingPlaceholder')}
                data-testid="commande-tracking-input"
              />
              <p className="text-xs text-gray-500">{t('cmd.trackingEmailNote')}</p>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowTrackingModal(false)} className="flex-1 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 font-bold">{t('common.cancel')}</button>
                <button
                  type="button"
                  onClick={saveTracking}
                  disabled={trackingSaving}
                  className="flex-1 py-3 rounded-xl btn-accent font-bold"
                  data-testid="commande-tracking-save"
                >
                  {trackingSaving ? '…' : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      </div>
    </Layout>
  );
};

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export default CommandesPage;
